import { useState } from "react";
// @ts-ignore
import { useTransitionCarousel } from "react-spring-carousel";
import type { TabOptions } from "src/lib/tabs-ui/TabsStore.tsx";
import { TabsStore } from "src/lib/tabs-ui/TabsStore.tsx";
import { Flex } from "src/components/Flex.tsx";
import * as styles from "./DetailedProductView.styles.ts";

import { SimpleTabsV2 } from "src/components/SimpleTabsV2/SimpleTabsV2.tsx";
import { H5 } from "src/components/HeadingV2/HeadingV2.tsx";
import { DefinitionList } from "../DefinitionList/DefinitionList.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import placeholderImageSrc from "src/assets/illustrations/placeholder_image.svg";
import {
  formatDate,
  formatPercents,
  formatPrice,
  formatWeight,
  toFixedWithoutZeros,
} from "@exonn/utils";
import { Currency } from "src/common/constants.ts";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { useExonnHubOrder } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubOrder.ts";
import { Counter } from "src/components/Counter/Counter.tsx";
import { useExonnHubContext } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubContext.ts";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import type { ExonnHubProduct as BackendExonnHubProduct } from "@exonn/js-sdk";
import { css, Global } from "@emotion/react";
import { Link } from "src/components/TextV2/Link.tsx";
import { BrokenImage } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/DetailedProductView/BrokenImage/BrokenImage.tsx";
import { MiniIconButton } from "src/components/MiniIconButton/MiniIconButton.tsx";
import { useIsDemoShop } from "src/hooks/use-is-demo-shop.ts";
import { useAddProductToOrder } from "src/admin/modules/b2b-gate-order/components/EditSupplierOrderForB2bGate/hooks/useAddProductToCart.ts";

const tabsStore = new TabsStore();

enum TabEnum {
  General = "general",
  Description = "description",
}

export const DetailedProductView = ({
  data,
}: {
  data: BackendExonnHubProduct;
}) => {
  const store = useExonnHubStore();
  const { orderId } = useExonnHubContext();
  const isDemoShop = useIsDemoShop();
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  const amountPerPackage = +data.amount_of_unit_per_package;
  const unitPrice = +data.unit_price;

  const saleMinQuantity = 1;
  const saleMaxQuantity = +data.stock;
  const slides = data.images.length
    ? data.images.map((item) => ({
        src: item.url,
        id: item.id,
        isPlaceholder: false,
      }))
    : [
        {
          src: placeholderImageSrc,
          isPlaceholder: true,
          id: "1",
        },
      ];
  const [activeItem, setActiveItem] = useState<string | undefined>(
    slides[0]?.id,
  );

  const { mutate: addProductToOrder } = useAddProductToOrder();

  const { data: order } = useExonnHubOrder({
    orderId,
  });

  const orderPosition = (order?.items || []).find(
    (item) => item.variantId === data.id,
  );

  const items = slides.map((item) => ({
    id: item.id,
    renderItem: slides.map((slide) => {
      if (slide.src === item.src) {
        return (
          <PhotoView src={item.src}>
            <div
              css={styles.image({
                src: item.src,
                isPlaceholder: item.isPlaceholder,
              })}
            />
          </PhotoView>
        );
      }
      // all slides have to be mounted at the same time
      // otherwise you won't be able to see all slides in full view gallery
      // this is how react-photo-view works
      return <PhotoView src={slide.src} />;
    }),
    renderThumb: (
      <div
        css={styles.dot({ isActive: item.id === activeItem })}
        onClick={() => slideToItem(item.id)}
      />
    ),
  }));

  const {
    carouselFragment,
    thumbsFragment,
    useListenToCustomEvent,
    slideToItem,
  } = useTransitionCarousel({
    withLoop: true,
    withThumbs: true,

    items: items,
  });

  const slideToNextItem = () => {
    const currentIndex = items.findIndex((item) => item.id === activeItem);
    const nextIndex = (currentIndex + 1) % items.length;

    slideToItem(items[nextIndex].id);
  };

  const slideToPrevItem = () => {
    const currentIndex = items.findIndex((item) => item.id === activeItem);
    const nextIndex = (currentIndex - 1 + items.length) % items.length;

    slideToItem(items[nextIndex].id);
  };

  useListenToCustomEvent((event: any) => {
    if (event.eventName === "onSlideStartChange") {
      setActiveItem(event.nextItem.id);
    }
  });

  const [activeTabId, setActiveTabId] = useState<string | undefined>(
    TabEnum.General,
  );
  const tabs: TabOptions[] = [
    {
      title: t("tabs.general"),
      id: TabEnum.General,
      content: (
        <GeneralTabContent onActiveTabChange={setActiveTabId} data={data} />
      ),
    },
    {
      title: t("tabs.description"),
      id: TabEnum.Description,
      content: <DescriptionTab data={data} />,
    },
  ];

  const updateOrderPosition = (quantity: number) => {
    addProductToOrder({
      data: {
        b2b_gate_order_id: orderId,
        quantity_ordered: quantity,
        variant_id: data.id,
        unit_price: unitPrice,
      },
      locale: "de",
    });
  };

  const handleAdd = () => {
    updateOrderPosition(amountPerPackage);
  };
  const handleChangeQuantity = (count: number) => {
    updateOrderPosition(amountPerPackage * count);
  };
  const handleDelete = () => {
    updateOrderPosition(0);
  };

  return (
    <div css={styles.root}>
      <div
        css={styles.closeBtnContainer}
        onClick={() => store.closeProductPanel()}
      >
        <TooltipV2 css={styles.closeBtn} content={t("closePreviewPanel")}>
          <i
            style={{
              cursor: "pointer",
            }}
            className={"icon-15-sb_arw_mx"}
          />
        </TooltipV2>
      </div>

      <Global
        styles={(theme) => css`
          .PhotoView-Portal {
            z-index: ${theme.zIndices.modals} !important;
          }
        `}
      />
      <div css={styles.sliderSection}>
        {items.length > 1 && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              slideToPrevItem();
            }}
            css={styles.arrowLeftContainer}
          >
            <MiniIconButton>
              <i className={"icon-15-arrw_l"} />
            </MiniIconButton>
          </div>
        )}

        {items.length > 1 && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              slideToNextItem();
            }}
            css={styles.arrowRightContainer}
          >
            <MiniIconButton>
              <i className={"icon-15-arrw_r"} />
            </MiniIconButton>
          </div>
        )}

        <div css={styles.carouselThumb}>
          <PhotoProvider brokenElement={<BrokenImage />} speed={() => 100}>
            {carouselFragment}
          </PhotoProvider>
        </div>
        <Flex css={styles.dotsContainer} style={{ marginTop: "5px" }}>
          {thumbsFragment}
        </Flex>
      </div>
      <div css={styles.tabsSection}>
        <SimpleTabsV2
          store={tabsStore}
          headerProps={{
            style: {
              top: "-10px",
              paddingTop: "10px",
              backgroundColor: "white",
              position: "sticky",
              height: "45px",
            },
          }}
          stickyHeadersEnabled={true}
          activeTabId={activeTabId}
          hasControlledActiveTabId
          onActiveTabIdChange={setActiveTabId}
          tabs={tabs}
        />
      </div>
      <Flex justifyContent={"center"} css={styles.footer}>
        {orderPosition ? (
          <Counter
            min={saleMinQuantity}
            max={saleMaxQuantity}
            onDelete={handleDelete}
            count={orderPosition.quantityInPackages}
            onChange={handleChangeQuantity}
          />
        ) : (
          <ButtonV2
            disabled={isDemoShop}
            variant={"filled"}
            skin={"info"}
            size={"sm"}
            startAdornment={<span className={"icon-15-add"} />}
            onClick={handleAdd}
          >
            {t("addProduct")}
          </ButtonV2>
        )}
      </Flex>
    </div>
  );
};

const DescriptionTab = ({ data }: { data: BackendExonnHubProduct }) => {
  return (
    <div css={styles.tabContent}>
      <div
        css={styles.fullDescription}
        dangerouslySetInnerHTML={{ __html: data.long_description }}
      />
    </div>
  );
};

const GeneralTabContent = ({
  data,
  onActiveTabChange,
}: {
  data: BackendExonnHubProduct;
  onActiveTabChange: (id: string) => void;
}) => {
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  const unitPrice = +data.unit_price;
  const unitNetWeight = +data.unit_net_weight;
  const vatPercent = +data.vat_percent;
  const bestBeforeDate = data.best_before_date;
  const amountPerPackage = +data.amount_of_unit_per_package;
  const viewFullDescription = () => {
    onActiveTabChange(TabEnum.Description);
  };
  const isDemoShop = useIsDemoShop();
  const store = useExonnHubStore();
  return (
    <div css={styles.tabContent}>
      <H5>{data.title}</H5>
      <DefinitionList
        style={{
          marginTop: "6px",
        }}
        // @ts-ignore
        definitions={[
          {
            label: t("previewPanel.properties.weight"),
            value: formatWeight(unitNetWeight, data.unit),
          },
          {
            label: "VPE (Verpackungseinheit)",
            value: amountPerPackage + " " + t("pc"),
          },
          {
            label: t("previewPanel.properties.vatPercent"),
            value: formatPercents(vatPercent),
          },
          {
            label: t("previewPanel.properties.stock"),
            value: toFixedWithoutZeros(+data.stock, 6),
          },
          bestBeforeDate && {
            label: t("previewPanel.properties.bestBeforeDate"),
            value: formatDate(bestBeforeDate),
          },
          {
            label: t("previewPanel.properties.ean"),
            value: data.ean,
          },
          {
            label: t("previewPanel.properties.unit_price"),
            value: isDemoShop ? (
              <Link
                onClick={() => {
                  store.drawAttentionToWarning({
                    intensifyPulsation: 4000,
                    shake: 750,
                  });
                }}
              >
                {t("registerToSeePrices")}
              </Link>
            ) : (
              formatPrice(unitPrice, Currency.EUR)
            ),
          },
        ].filter(Boolean)}
      />
      <div css={styles.descriptionSection}>
        <H5>{t("productShortDescription")}:</H5>
        <TextV2 css={styles.descriptionText} variant={"body1"}>
          {data.short_description}
        </TextV2>
        &nbsp;
        <Link onClick={viewFullDescription}>{t("viewFullDescription")}</Link>
      </div>
    </div>
  );
};
